import { createEffect, createEvent, createStore, sample } from 'effector'

import {
  $selectedCategory,
  setSelectedCategory,
} from '@/models/vault/categories.ts'
import {
  $selectedMediaIds,
  $vaultMediasMap,
  refetchVaultMedias,
  toggleSelectedMedia,
} from '@/models/vault/medias/index.ts'
import { getStreamingUrlFx } from '@/models/vault/medias/streams.ts'
import { closeVault } from '@/models/vault/vault.ts'
import type { StreamableVaultMedia } from '@/modules/vault/types.ts'
import { isStreamable } from '@/modules/vault/utils.ts'

export const setSelectedMedia = createEvent<Nullable<StreamableVaultMedia>>(
  'set selected vault media',
)
/**
 * Media to preview.
 */
export const $selectedMedia = createStore<Nullable<StreamableVaultMedia>>(
  null,
  {
    name: 'vault selected media',
  },
)
  .on(setSelectedMedia, (_, value) => value)
  .reset(closeVault)

export const setMediaSource = createEvent<Nullable<string>>(
  'set media streaming source',
)
export const $mediaSource = createStore<Nullable<string>>(null, {
  name: 'vault media streaming source',
}).on(setMediaSource, (_, value) => value)

export const setLastSelectedMediaId = createEvent<Nullable<number>>(
  'set last selected media id',
)
/**
 * Id of last media that user clicks, user to auto-scroll to the last edited place, after we reload a media list.
 */
export const $lastSelectedMediaId = createStore<Nullable<number>>(null, {
  name: 'last selected media to scroll',
}).on(setLastSelectedMediaId, (_, id) => id)

// load a media source for a selected media asset
sample({
  clock: $selectedMedia,
  source: {
    selectedMedia: $selectedMedia,
  },
  filter: ({ selectedMedia }) => {
    return isStreamable(selectedMedia) && !!selectedMedia?.id
  },
  fn: ({ selectedMedia }) => selectedMedia!,
  target: createEffect(async (selectedMedia: StreamableVaultMedia) => {
    const url = await getStreamingUrlFx({
      mediaId: selectedMedia.id,
      isExpirable: selectedMedia.isExpirable,
      messageId: selectedMedia.relatedMessageId!,
      userId: selectedMedia.relatedUserId,
    })

    setMediaSource(url)
  }),
})

// reset a media source
sample({
  clock: $selectedMedia,
  source: {
    selectedMedia: $selectedMedia,
  },
  filter: ({ selectedMedia }) => {
    return !isStreamable(selectedMedia) || !selectedMedia?.id
  },
  fn: () => null,
  target: setMediaSource,
})

/**
 * We need to handle manual clicks and ignore `select all` button logic
 */
sample({
  clock: toggleSelectedMedia,
  source: { selectedIds: $selectedMediaIds },
  fn: ({ selectedIds }, media) =>
    // remember last selected id to scroll it
    selectedIds.includes(media.id)
      ? media.id
      : selectedIds[selectedIds.length - 1] || null,
  target: setLastSelectedMediaId,
})

/**
 * Reset selected media when we switch to another category, even if media could exist there.
 */
sample({
  clock: setSelectedCategory,
  source: {
    selectedCategory: $selectedCategory,
  },
  filter: ({ selectedCategory }, category) =>
    selectedCategory?.id !== category?.id,
  fn: () => null,
  target: setLastSelectedMediaId,
})

/**
 * When we reset a media list, we need to calculate, should we scroll to the last selected media or not.
 */
sample({
  clock: refetchVaultMedias,
  source: {
    vaultMap: $vaultMediasMap,
    selectedCategory: $selectedCategory,
    lastSelectedMediaId: $lastSelectedMediaId,
  },
  filter: ({ lastSelectedMediaId, selectedCategory, vaultMap }) =>
    !!lastSelectedMediaId &&
    !!selectedCategory &&
    !vaultMap[lastSelectedMediaId]?.listStates.includes(selectedCategory.id),
  fn: () => null,
  target: setLastSelectedMediaId,
})
